import "@xyflow/react/dist/style.css"

import { useMemo, useState } from "react"

import { LIGHT_BLUE, PRIMARY } from "@constants/colors"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FitScreenIcon from "@mui/icons-material/FitScreen"
import SearchIcon from "@mui/icons-material/Search"
import ZoomInIcon from "@mui/icons-material/ZoomIn"
import ZoomOutIcon from "@mui/icons-material/ZoomOut"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import type { Edge, Node, ReactFlowInstance } from "@xyflow/react"
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow
} from "@xyflow/react"
import {
  AI_FLOW_ENGINE_OPTIONS,
  AiFlowEngine,
  V1_ENTRY_NODE_ID
} from "./engines"
import {
  AI_FLOW_CHANNEL_OPTIONS,
  AI_FLOW_EDGES,
  AI_FLOW_NODES,
  AiFlowChannel,
  AiFlowNodeInfo,
  AiFlowNodeKind
} from "./flowData"
import type { LivePrompt } from "./livePrompts"
import { useLivePrompts } from "./useLivePrompts"
import V2Guide from "./v2/V2Guide"

const DEFAULT_ENGINE: AiFlowEngine = "v2"

type FlowNodeData = Record<string, unknown> & {
  label: JSX.Element
  infoId: string
}

const KIND_COLOR: Record<AiFlowNodeKind, string> = {
  platform: "#157CFC",
  context: "#0F9D58",
  guard: "#E09100",
  prompt: "#7B4DFF",
  decision: "#D84315",
  action: "#006D75",
  rag: "#5F6368",
  skill: "#1B5E20",
  workflow: "#6A1B9A",
  mcp: "#00796B",
  security: "#AD1457",
  transaction: "#C62828",
  audit: "#455A64",
  output: "#1E7B34"
}

/**
 * This map shows the flow diagram which prompt backs each node, so it wants the SHARED row —
 * the text every role falls back to. A role override is a variation of the same node, not a
 * different node, so it must not replace the shared entry here.
 */
function getPromptMap(prompts: LivePrompt[]): Record<string, LivePrompt> {
  const baseRows = prompts.filter((prompt) => !prompt.engineVersion)
  return baseRows.reduce<Record<string, LivePrompt>>((acc, prompt) => {
    if (!acc[prompt.name] || !prompt.role) {
      acc[prompt.name] = prompt
    }
    return acc
  }, {})
}

/**
 * Lanes are filtered, the rest is only dimmed. A lane view is a different subgraph — showing
 * the whole diagram with 40 unrelated nodes greyed out defeats the point of picking a lane —
 * while a message-type view is the SAME graph seen from one channel, so dimming reads better
 * there. Adding a lane means adding it here, not another `channel === "..."` branch downstream.
 */
const LANE_CHANNELS = new Set<AiFlowChannel>(["skills", "commerce"])

function isLaneChannel(channel: AiFlowChannel) {
  return LANE_CHANNELS.has(channel)
}

function matchesChannel(info: AiFlowNodeInfo, channel: AiFlowChannel) {
  return channel === "all" || info.channels.includes(channel)
}

function getKindLabel(kind: AiFlowNodeKind) {
  const labels: Record<AiFlowNodeKind, string> = {
    platform: "Platform",
    context: "Context",
    guard: "Guard",
    prompt: "Prompt",
    decision: "Decision",
    action: "Action",
    rag: "RAG",
    skill: "Skill",
    workflow: "Workflow",
    mcp: "MCP",
    security: "Security",
    transaction: "Transaction",
    audit: "Audit",
    output: "Output"
  }
  return labels[kind]
}

function nodeMatchesSearch(info: AiFlowNodeInfo, term: string) {
  if (!term) return true
  const haystack = [
    info.title,
    info.shortTitle,
    info.subtitle,
    info.productSummary,
    info.stage,
    ...info.details,
    ...info.inputs,
    ...info.outputs,
    ...(info.decisionOutcomes ?? []),
    ...(info.promptNames ?? []).map((prompt) => prompt.name),
    ...info.codeRefs
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
  return haystack.includes(term.toLowerCase())
}

function AiFlowNodeLabel({
  info,
  isDimmed
}: {
  info: AiFlowNodeInfo
  isDimmed: boolean
}) {
  const color = KIND_COLOR[info.kind]
  return (
    <Box sx={{ width: 230, opacity: isDimmed ? 0.42 : 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: color,
            flex: "0 0 auto"
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.1
          }}
        >
          {info.stage}
        </Typography>
      </Stack>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.2 }}
      >
        {info.shortTitle || info.title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          mt: 0.5,
          lineHeight: 1.35
        }}
      >
        {info.subtitle}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap" }}>
        {info.promptNames?.slice(0, 2).map((prompt) => (
          <Chip
            key={prompt.name}
            label={prompt.name}
            size="small"
            sx={{
              height: 20,
              maxWidth: 150,
              fontSize: 10,
              bgcolor: LIGHT_BLUE,
              color: PRIMARY,
              "& .MuiChip-label": { px: 0.75 }
            }}
          />
        ))}
      </Stack>
    </Box>
  )
}

function DetailList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>
        {items.map((item) => (
          <Typography
            key={item}
            variant="body2"
            sx={{ color: "text.secondary", lineHeight: 1.45 }}
          >
            • {item}
          </Typography>
        ))}
      </Stack>
    </Box>
  )
}

function PromptDetails({
  info,
  promptMap,
  promptsLoading,
  promptsError
}: {
  info: AiFlowNodeInfo
  promptMap: Record<string, LivePrompt>
  promptsLoading: boolean
  promptsError: boolean
}) {
  if (!info.promptNames?.length) return null

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
        Prompt Detayları
      </Typography>
      {promptsLoading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Canlı promptlar yükleniyor
          </Typography>
        </Stack>
      )}
      {promptsError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Canlı promptlar alınamadı. Prompt adları ve placeholder açıklamaları
          gösteriliyor.
        </Alert>
      )}
      <Stack spacing={1}>
        {info.promptNames.map((promptRef) => {
          const prompt = promptMap[promptRef.name]
          return (
            <Accordion key={promptRef.name} variant="outlined" disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {promptRef.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {promptRef.usage}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10
                  }}
                >
                  <>
                    {prompt ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {prompt.aiModel && (
                          <Chip
                            size="small"
                            label={`Model: ${prompt.aiModel}`}
                          />
                        )}
                        <Chip
                          size="small"
                          label={`Role: ${prompt.role ?? "All roles"}`}
                        />
                        {prompt.usage && (
                          <Chip size="small" label={`Usage: ${prompt.usage}`} />
                        )}
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        Bu prompt canlı listede bulunamadı veya erişim yok.
                      </Alert>
                    )}
                    {!!promptRef.placeholders?.length && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontWeight: 800, mb: 0.5 }}
                        >
                          Placeholder mantığı
                        </Typography>
                        <Stack spacing={0.5}>
                          {promptRef.placeholders.map((placeholder) => (
                            <Typography
                              key={placeholder.token}
                              variant="caption"
                              sx={{ color: "text.secondary", lineHeight: 1.45 }}
                            >
                              <Box component="code">{placeholder.token}</Box>:{" "}
                              {placeholder.meaning}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {prompt?.responseSchema && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontWeight: 800, mb: 0.5 }}
                        >
                          Response schema
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1,
                            bgcolor: "#F7F7FA",
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            overflow: "auto",
                            fontSize: 12,
                            maxHeight: 180
                          }}
                        >
                          {JSON.stringify(prompt.responseSchema, null, 2)}
                        </Box>
                      </Box>
                    )}
                    {prompt?.description && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontWeight: 800, mb: 0.5 }}
                        >
                          Canlı prompt metni
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 1.25,
                            bgcolor: "#111827",
                            color: "#F9FAFB",
                            borderRadius: 1,
                            whiteSpace: "pre-wrap",
                            overflow: "auto",
                            fontSize: 12,
                            lineHeight: 1.45,
                            maxHeight: 320
                          }}
                        >
                          {prompt.description}
                        </Box>
                      </Box>
                    )}
                  </>
                </div>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Stack>
    </Box>
  )
}

export default function AiFlowPage() {
  const [engine, setEngine] = useState<AiFlowEngine>(DEFAULT_ENGINE)
  const [channel, setChannel] = useState<AiFlowChannel>("all")
  const [search, setSearch] = useState("")
  const flowNodes = AI_FLOW_NODES
  const flowEdges = AI_FLOW_EDGES
  const engineOption =
    AI_FLOW_ENGINE_OPTIONS.find((item) => item.value === engine) ||
    AI_FLOW_ENGINE_OPTIONS[0]
  const [selectedNodeId, setSelectedNodeId] = useState<string>(V1_ENTRY_NODE_ID)
  const [detailOpen, setDetailOpen] = useState(true)
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance<
    Node<FlowNodeData>,
    Edge
  > | null>(null)

  const promptsQuery = useLivePrompts()

  const promptMap = useMemo(
    () => getPromptMap(promptsQuery.data || []),
    [promptsQuery.data]
  )

  const visibleInfos = useMemo(() => {
    return flowNodes.filter((info) => {
      if (isLaneChannel(channel) && !info.channels.includes(channel)) {
        return false
      }
      return nodeMatchesSearch(info, search.trim())
    })
  }, [channel, flowNodes, search])

  const visibleIds = useMemo(
    () => new Set(visibleInfos.map((info) => info.id)),
    [visibleInfos]
  )

  const nodes = useMemo<Node<FlowNodeData>[]>(() => {
    return visibleInfos.map((info) => {
      const active = matchesChannel(info, channel)
      const color = KIND_COLOR[info.kind]
      const isSelected = selectedNodeId === info.id
      return {
        id: info.id,
        position: info.position,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        data: {
          infoId: info.id,
          label: <AiFlowNodeLabel info={info} isDimmed={!active} />
        },
        type: "default",
        style: {
          width: 260,
          minHeight: 132,
          padding: 14,
          borderRadius: 8,
          border: isSelected ? `2px solid ${PRIMARY}` : `1px solid ${color}`,
          boxShadow: isSelected
            ? "0 12px 30px rgba(21, 124, 252, 0.22)"
            : "0 8px 24px rgba(15, 23, 42, 0.08)",
          background: active ? "#FFFFFF" : "#F4F6F8"
        }
      }
    })
  }, [channel, selectedNodeId, visibleInfos])

  const edges = useMemo<Edge[]>(() => {
    return flowEdges
      .filter(
        (edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target)
      )
      .map((edge) => {
        const edgeChannels = edge.channels || ["all"]
        const active =
          channel === "all" ||
          edgeChannels.includes("all") ||
          edgeChannels.includes(channel)
        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label,
          type: "step",
          markerEnd: { type: MarkerType.ArrowClosed },
          // Declared on the edge itself (`animated`) instead of matched against the label text —
          // a renamed label used to silently stop animating.
          animated: active && Boolean(edge.animated),
          style: {
            stroke: active ? PRIMARY : "#B8C1CC",
            strokeWidth: active ? 2.2 : 1,
            opacity: active ? 0.95 : 0.35
          },
          labelStyle: {
            fill: active ? "#111827" : "#8A94A6",
            fontWeight: 700,
            fontSize: 11
          },
          labelBgStyle: {
            fill: "#FFFFFF",
            fillOpacity: 0.9
          }
        }
      })
  }, [channel, flowEdges, visibleIds])

  const selectedInfo =
    flowNodes.find((info) => info.id === selectedNodeId) || flowNodes[0]

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Paper
        variant="outlined"
        square
        sx={{
          p: 1.5,
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap"
        }}
      >
        <Box sx={{ minWidth: 240 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            AI Flow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {engineOption.caption}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 230 }}>
          <InputLabel>Motor</InputLabel>
          <Select
            label="Motor"
            value={engine}
            onChange={(event) => {
              const nextEngine = event.target.value as AiFlowEngine
              setEngine(nextEngine)
              setChannel("all")
              setSelectedNodeId(V1_ENTRY_NODE_ID)
              setDetailOpen(true)
            }}
          >
            {AI_FLOW_ENGINE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {engine === "v1" && (
          <>
            <FormControl size="small" sx={{ minWidth: 210 }}>
              <InputLabel>Akış filtresi</InputLabel>
              <Select
                label="Akış filtresi"
                value={channel}
                onChange={(event) => {
                  const nextChannel = event.target.value as AiFlowChannel
                  setChannel(nextChannel)
                  if (
                    isLaneChannel(nextChannel) &&
                    !flowNodes
                      .find((node) => node.id === selectedNodeId)
                      ?.channels.includes(nextChannel)
                  ) {
                    setSelectedNodeId(V1_ENTRY_NODE_ID)
                  }
                  window.requestAnimationFrame(() =>
                    flowInstance?.fitView({ padding: 0.18 })
                  )
                }}
              >
                {AI_FLOW_CHANNEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Node, prompt veya kod referansı ara"
              sx={{ minWidth: 320, flex: "1 1 320px", maxWidth: 560 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <Tooltip title="Yakınlaştır">
              <IconButton size="small" onClick={() => flowInstance?.zoomIn()}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Uzaklaştır">
              <IconButton size="small" onClick={() => flowInstance?.zoomOut()}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ekrana sığdır">
              <IconButton
                size="small"
                onClick={() => flowInstance?.fitView({ padding: 0.18 })}
              >
                <FitScreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Paper>

      {engine === "v2" ? (
        <V2Guide
          prompts={promptsQuery.data || []}
          promptsLoading={promptsQuery.isLoading}
          promptsFailed={promptsQuery.isError}
        />
      ) : (
        <>
          <Box sx={{ flex: 1, minHeight: 0, bgcolor: "#F7F8FA" }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable
              fitView
              minZoom={0.22}
              maxZoom={1.25}
              onInit={(instance) => setFlowInstance(instance)}
              onNodeClick={(_event, node) => {
                setSelectedNodeId(node.id)
                setDetailOpen(true)
              }}
            >
              <Background color="#D8DEE8" gap={24} />
              <Controls showInteractive={false} />
              <MiniMap
                pannable
                zoomable
                nodeColor={(node) => {
                  const info = flowNodes.find((item) => item.id === node.id)
                  return info ? KIND_COLOR[info.kind] : PRIMARY
                }}
                style={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  overflow: "hidden"
                }}
              />
            </ReactFlow>
          </Box>

          <Drawer
            anchor="right"
            open={detailOpen}
            variant="persistent"
            PaperProps={{
              sx: {
                width: 430,
                maxWidth: "42vw",
                borderLeft: 1,
                borderColor: "divider"
              }
            }}
          >
            <Stack sx={{ height: "100%" }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={getKindLabel(selectedInfo.kind)}
                        sx={{
                          bgcolor: KIND_COLOR[selectedInfo.kind],
                          color: "#FFFFFF",
                          fontWeight: 800
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {selectedInfo.stage}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 900 }}>
                      {selectedInfo.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedInfo.subtitle}
                    </Typography>
                  </Box>
                  <Tooltip title="Paneli gizle">
                    <IconButton
                      size="small"
                      onClick={() => setDetailOpen(false)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              <Stack spacing={2} sx={{ p: 2, overflow: "auto" }}>
                <Alert severity="info">{selectedInfo.productSummary}</Alert>

                <DetailList
                  title="Ürün diliyle ne oluyor?"
                  items={selectedInfo.details}
                />
                <DetailList title="Girdiler" items={selectedInfo.inputs} />
                <DetailList title="Çıktılar" items={selectedInfo.outputs} />
                <DetailList
                  title="Karar / sonuç seçenekleri"
                  items={selectedInfo.decisionOutcomes ?? []}
                />

                <PromptDetails
                  info={selectedInfo}
                  promptMap={promptMap}
                  promptsLoading={promptsQuery.isLoading}
                  promptsError={promptsQuery.isError}
                />

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, mb: 1 }}
                  >
                    Kod referansları
                  </Typography>
                  <Stack spacing={0.75}>
                    {selectedInfo.codeRefs.map((ref) => (
                      <Box
                        key={ref}
                        component="code"
                        sx={{
                          display: "block",
                          p: 1,
                          bgcolor: "#F7F7FA",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          fontSize: 12,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere"
                        }}
                      >
                        {ref}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Drawer>
        </>
      )}
    </Box>
  )
}
